import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";

import Form from "../forms/Form";
import SaleFormModal from "../modals/SaleFormModal";

const NAV_ITEMS = [
    { to: "/", icon: "fas fa-home", label: "Dashboard", exact: true },
    { to: "/customers", icon: "fas fa-user", label: "Customers" },
    { to: "/sales", icon: "fas fa-shopping-cart", label: "Sales" },
    { to: "/work-orders", icon: "far fa-calendar-check", label: "Work Orders" },
    { to: "/summary-reports", icon: "fas fa-chart-bar", label: "Reports" },
    { to: "/admin", icon: "fas fa-sliders-h", label: "Admin" },
];

const sidebarPrimaryRowClass = (collapsed) =>
    `group flex rounded-lg text-sm font-medium text-ink-600 !no-underline transition-colors hover:bg-ink-100 hover:text-ink-900 hover:!no-underline active:!no-underline focus:!no-underline ${
        collapsed ? "justify-center px-1 py-3 m-1" : "items-center gap-3 px-3 py-2"
    }`;

const sidebarPrimaryIconClass = (collapsed, iconClass) =>
    `${iconClass} shrink-0 text-center !no-underline text-brand-500 group-hover:text-brand-700 ${
        collapsed ? "text-xl w-8 leading-none" : "w-5 text-base leading-none"
    }`;

/** Footer "Add new" — primary CTA; circle when collapsed (see .app-sidebar-add-collapsed in index.css vs Bootstrap) */
const sidebarFooterAddClass = (collapsed) =>
    collapsed
        ? "app-sidebar-add-collapsed group flex flex-none items-center justify-center rounded-full m-1 p-0 text-sm font-medium !no-underline bg-brand-600 text-white transition-colors hover:bg-brand-700 hover:!no-underline active:!no-underline focus:!no-underline"
        : "group flex w-full justify-center items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium !no-underline text-white transition-colors hover:bg-brand-700 hover:!no-underline active:!no-underline focus:!no-underline";

const sidebarFooterAddIconClass = (collapsed) =>
    `fas fa-plus shrink-0 text-center !no-underline text-white ${
        collapsed ? "text-xl w-8 leading-none" : "w-5 text-base leading-none"
    }`;

const Logo = ({ collapsed = false, mobileTopBar = false }) => (
    <div
        className={`flex items-center justify-center gap-2 ${
            collapsed || mobileTopBar ? "justify-center" : ""
        } ${mobileTopBar ? "min-w-0 max-w-full" : ""}`}
        title={collapsed || mobileTopBar ? "Summit Doors" : undefined}
    >
        <img
            src="https://cdn.prod.website-files.com/63ffce129be7494e7cb1c6a2/65e79afd60fde8ed10fe2853_new%20logo.png"
            alt="Summit Doors logo"
            style={
                mobileTopBar
                    ? {
                          height: "auto",
                          maxHeight: "2.35rem",
                          width: "auto",
                          maxWidth: "100%",
                          objectFit: "contain",
                      }
                    : {
                          height: collapsed ? "3rem" : "150px",
                          width: "auto",
                          maxWidth: collapsed ? "3.25rem" : "none",
                          objectFit: "contain",
                      }
            }
        />
    </div>
);

const SIDEBAR_COLLAPSED_KEY = "summit-doors-sidebar-collapsed";

const DESKTOP_MIN_WIDTH = 768;

// Matches Tailwind w-60 / w-16 so the control stays centered on the sidebar edge when collapsed or expanded
const RAIL_RIGHT_EXPANDED = "15rem";
const RAIL_RIGHT_COLLAPSED = "4rem";

const RAIL_TOGGLE_BORDER = "#e2e8f0";
const RAIL_TOGGLE_ICON = "#94a3b8";
const RAIL_TOGGLE_SHADOW = "0 2px 8px rgba(15, 23, 42, 0.12)";
const RAIL_TOGGLE_SHADOW_HOVER = "0 3px 10px rgba(15, 23, 42, 0.16)";

const sidebarRailToggleStyle = (left) => ({
    position: "fixed",
    top: "2.25rem",
    left,
    transform: "translateX(-50%)",
    zIndex: 10050,
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    margin: 0,
    padding: 0,
    borderRadius: "50%",
    border: `1px solid ${RAIL_TOGGLE_BORDER}`,
    backgroundColor: "#ffffff",
    boxShadow: RAIL_TOGGLE_SHADOW,
    cursor: "pointer",
    color: RAIL_TOGGLE_ICON,
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1,
    transition: "left 0.2s ease, box-shadow 0.15s ease, background-color 0.15s ease",
});

const Navbar = (props) => {
    const desktopAsideRef = useRef(null);
    const [sidebarRightPx, setSidebarRightPx] = useState(null);

    const [showCustomerForm, setShowCustomerForm] = useState(false);
    const [openSaleForm, setOpenSaleForm] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [desktopLayout, setDesktopLayout] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches;
    });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        if (typeof window === "undefined") return false;
        if (window.innerWidth < DESKTOP_MIN_WIDTH) return true;
        try {
            return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
        } catch {
            return false;
        }
    });
    const location = useLocation();

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`);
        const sync = () => setDesktopLayout(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        if (!desktopLayout) return;
        try {
            window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [sidebarCollapsed, desktopLayout]);

    useEffect(() => {
        if (!desktopLayout) {
            setSidebarCollapsed(true);
            return;
        }
        try {
            setSidebarCollapsed(window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1");
        } catch {
            setSidebarCollapsed(false);
        }
    }, [desktopLayout]);

    useEffect(() => {
        if (!desktopLayout || typeof window === "undefined") {
            setSidebarRightPx(null);
            return;
        }

        const aside = desktopAsideRef.current;
        if (!aside) return;

        const update = () => {
            window.requestAnimationFrame(() => {
                const el = desktopAsideRef.current;
                if (!el) return;
                setSidebarRightPx(el.getBoundingClientRect().right);
            });
        };

        update();

        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => {
                window.requestAnimationFrame(update);
            });
            ro.observe(aside);
        }

        window.addEventListener("resize", update);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [desktopLayout, sidebarCollapsed]);

    const navItem = (item, onClick, collapsed) => (
        <NavLink
            key={item.to}
            to={item.to}
            exact={item.exact}
            onClick={onClick}
            className={sidebarPrimaryRowClass(collapsed)}
            activeClassName="!bg-brand-50 !text-brand-700 !no-underline"
            aria-label={item.label}
            title={collapsed ? item.label : undefined}
        >
            <i className={sidebarPrimaryIconClass(collapsed, item.icon)} aria-hidden="true" />
            {!collapsed && <span className="!no-underline">{item.label}</span>}
        </NavLink>
    );

    const sidebarBody = (variant) => {
        const collapsed = variant === "sidebar" && sidebarCollapsed;

        return (
            <div className="flex min-h-0 flex-1 flex-col">
                {variant !== "drawer" && (
                    <div className={`py-4 ${collapsed ? "flex justify-center px-2" : "px-4"}`}>
                        <Logo collapsed={collapsed} />
                    </div>
                )}
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                    <nav
                        aria-label="Primary"
                        className={`app-sidebar-nav ${collapsed ? "space-y-3 px-1.5 pb-2" : "space-y-1 px-3 pb-2"}`}
                    >
                        {NAV_ITEMS.map((item) =>
                            navItem(
                                item,
                                variant === "drawer" ? () => setDrawerOpen(false) : undefined,
                                collapsed,
                            ),
                        )}
                    </nav>
                    <div
                        className={`app-sidebar-nav app-sidebar-footer border-t border-ink-100 ${
                            collapsed
                                ? "app-sidebar-footer--collapsed flex flex-col items-center px-1.5 pb-3 pt-2"
                                : "px-3 py-3"
                        }`}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                setShowCustomerForm(true);
                                setDrawerOpen(false);
                            }}
                            className={sidebarFooterAddClass(collapsed)}
                            aria-label="Add new"
                            title={collapsed ? "Add new" : undefined}
                        >
                            <i className={sidebarFooterAddIconClass(collapsed)} aria-hidden="true" />
                            {!collapsed && <span className="!no-underline">Add new</span>}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-ink-50">
            {/* Mobile top bar: menu + logo only (wordmark and add live in the drawer). */}
            <header className="sticky top-0 z-30 flex h-14 items-center border-b border-ink-100 bg-white px-3 md:hidden">
                <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Open navigation menu"
                    aria-expanded={drawerOpen}
                    className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100"
                >
                    <i className="fas fa-bars" aria-hidden="true" />
                </button>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-12">
                    <div className="pointer-events-auto min-w-0 max-w-[min(80vw,16rem)]">
                        <Logo mobileTopBar />
                    </div>
                </div>
            </header>

            {/* Mobile drawer */}
            {drawerOpen && (
                <div
                    className="fixed inset-0 z-40 md:hidden"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Main navigation"
                >
                    <div
                        className="absolute inset-0 bg-ink-900 bg-opacity-50"
                        onClick={() => setDrawerOpen(false)}
                        aria-hidden="true"
                    />
                    <aside className="absolute inset-y-0 left-0 flex min-h-0 w-72 max-w-[85vw] flex-col bg-white shadow-elevated">
                        <div className="flex h-14 shrink-0 items-center justify-end border-b border-ink-100 px-4">
                            <button
                                type="button"
                                onClick={() => setDrawerOpen(false)}
                                aria-label="Close navigation menu"
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100"
                            >
                                <i className="fas fa-times" aria-hidden="true" />
                            </button>
                        </div>
                        {sidebarBody("drawer")}
                    </aside>
                </div>
            )}

            {/* Desktop sidebar */}
            <aside
                ref={desktopAsideRef}
                id="app-desktop-sidebar"
                className={`fixed inset-y-0 left-0 z-30 hidden min-h-0 flex-col overflow-x-hidden border-r border-ink-100 bg-white transition-[width] duration-200 ease-out md:flex ${
                    sidebarCollapsed ? "w-16" : "w-60"
                }`}
            >
                {sidebarBody("sidebar")}
            </aside>

            {/* Portal + inline styles: avoids Tailwind hidden/md conflicts, overflow, and z-stacking */}
            {desktopLayout &&
                createPortal(
                    <button
                        type="button"
                        onClick={() => setSidebarCollapsed((c) => !c)}
                        aria-expanded={!sidebarCollapsed}
                        aria-controls="app-desktop-sidebar"
                        aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        style={sidebarRailToggleStyle(
                            sidebarRightPx != null
                                ? `${sidebarRightPx}px`
                                : sidebarCollapsed
                                  ? RAIL_RIGHT_COLLAPSED
                                  : RAIL_RIGHT_EXPANDED,
                        )}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f8fafc";
                            e.currentTarget.style.boxShadow = RAIL_TOGGLE_SHADOW_HOVER;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#ffffff";
                            e.currentTarget.style.boxShadow = RAIL_TOGGLE_SHADOW;
                        }}
                    >
                        <span aria-hidden="true">{sidebarCollapsed ? "\u203a" : "\u2039"}</span>
                    </button>,
                    document.body,
                )}

            {/* Main content: inline padding-left keeps offset in sync with desktopLayout (768px)
                and beats any third-party CSS that might reset Tailwind responsive padding. */}
            <main
                className="relative z-10 min-w-0 transition-[padding-left] duration-200 ease-out"
                style={
                    desktopLayout
                        ? {
                              paddingLeft: sidebarCollapsed ? RAIL_RIGHT_COLLAPSED : RAIL_RIGHT_EXPANDED,
                          }
                        : undefined
                }
            >
                <div className="container-page py-6 sm:py-8">{props.children}</div>
            </main>

            <Form
                show={showCustomerForm}
                onHide={() => setShowCustomerForm(false)}
                showModal={showCustomerForm}
                setShowModal={setShowCustomerForm}
                setOpen={setOpenSaleForm}
            />
            <SaleFormModal
                open={openSaleForm}
                setOpen={setOpenSaleForm}
                setShowModal={setShowCustomerForm}
                handleClose={() => setOpenSaleForm(false)}
                handleClickOpen={() => setOpenSaleForm(true)}
            />
        </div>
    );
};

export default Navbar;
